import React, { useEffect, useState } from 'react';
import { Modal, MultiSelect, Button, Loader, Table, Text, Select, Group } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import type { ExternalObjectConf } from '@bageltechnologies/bagel-common';
import type { IntegrationTask } from '../../../../../api/integrations2.0';

interface RunModalProps {
  opened: boolean;
  onClose: () => void;
  externalObjects: { name: string; label?: string }[];
  externalObject: ExternalObjectConf | null;
  isLoadingObject: boolean;
  selectedObjects: string[];
  setSelectedObjects: (ids: string[]) => void;
  onRun: (dateRange?: { start: string; end: string; dateField?: string }) => void;
  isRunning: boolean;
  integrationKey: string;
  activeTasks: IntegrationTask[];
}

export const RunModal: React.FC<RunModalProps> = ({
  opened,
  onClose,
  externalObjects,
  externalObject,
  isLoadingObject,
  selectedObjects,
  setSelectedObjects,
  onRun,
  isRunning,
  integrationKey,
  activeTasks,
}) => {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [dateField, setDateField] = useState<string>('updatedAt');

  // Reset date range when modal opens
  useEffect(() => {
    if (opened) {
      // Set default date range
      const now = new Date();
      const daysAgo = new Date();
      daysAgo.setDate(now.getDate() - 30);
      
      setStartDate(daysAgo);
      setEndDate(now);
      setDateField('updatedAt');
    }
  }, [opened]);

  // Preselect the current object if selectedObjects is empty
  useEffect(() => {
    if (opened && externalObject) {
      setSelectedObjects([externalObject.name]);
    }
  }, [opened, externalObject]);

  const handleRun = () => {
    const dateRange = startDate && endDate ? {
      start: startDate.toISOString(),
      end: endDate.toISOString(),
      dateField: dateField
    } : undefined;
    
    onRun(dateRange);
  };

  // Filter active tasks for selected objects
  const relevantTasks = activeTasks.filter(task => 
    selectedObjects.length === 0 || selectedObjects.includes(task.objectName)
  );

  // Get status color for badges
  const getStatusColor = (status: string) => {
    switch (status) {
    case 'in_progress': return 'blue';
    case 'pending': return 'gray';
    case 'completed': return 'green';
    case 'failed': return 'red';
    default: return 'blue';
    }
  };

  return (
    <Modal 
      opened={opened} 
      onClose={onClose} 
      title={`Create Sync Run${externalObject ? ` for ${externalObject.label || externalObject.name || externalObject.originObject}` : ''}`} 
      centered 
      size="lg"
    >
      {isLoadingObject ? (
        <Loader />
      ) : (
        <>
          {/* Active tasks table on top */}
          <Text fw={500} mb="xs">Active Tasks</Text>
          <Table mb="md" striped withBorder style={{tableLayout: 'fixed', width: '100%' }}>
            <thead style={{ display: 'block', width: '100%' }}>
              <tr style={{ display: 'table', width: '100%', tableLayout: 'fixed' }}>
                <th style={{ width: '20%' }}>Object</th>
                <th style={{ width: '15%' }}>Status</th>
                <th style={{ width: '20%' }}>Started</th>
                <th style={{ width: '20%' }}>Stats</th>
                <th style={{ width: '25%' }}>Date Range</th>
              </tr>
            </thead>
            <tbody style={{ display: 'block', maxHeight: '200px', overflowY: 'auto', width: '100%' }}>
              {relevantTasks.length > 0 ? (
                relevantTasks.map((task) => (
                  <tr key={task._id} style={{ display: 'table', width: '100%', tableLayout: 'fixed' }}>
                    <td style={{ width: '20%' }}>
                      <Text size="sm">{task.objectName}</Text>
                    </td>
                    <td style={{ width: '15%' }}>
                      <Text 
                        size="xs" 
                        color={getStatusColor(task.execution.status)}
                        fw={500}
                      >
                        {task.execution.status.replace('_', ' ')}
                      </Text>
                    </td>
                    <td style={{ width: '20%' }}>
                      <Text size="xs">
                        {task.execution.startDate ? new Date(task.execution.startDate).toLocaleString() : '-'}
                      </Text>
                    </td>
                    <td style={{ width: '20%' }}>
                      <Text size="xs" color="dimmed">
                        {task.execution.stats ? (
                          `${task.execution.stats.processedCount || 0}/${task.execution.stats.receivedCount || 0} processed`
                        ) : (
                          '-'
                        )}
                      </Text>
                    </td>
                    <td style={{ width: '25%' }}>
                      <Text size="xs" color="dimmed">
                        {task.options?.startDate && task.options?.endDate ? (
                          `${new Date(task.options.startDate).toLocaleDateString()} - ${new Date(task.options.endDate).toLocaleDateString()}`
                        ) : (
                          '-'
                        )}
                      </Text>
                    </td>
                  </tr>
                ))
              ) : (
                <tr style={{ display: 'table', width: '100%', tableLayout: 'fixed' }}>
                  <td colSpan={5} style={{ textAlign: 'center', color: '#888', fontSize: 13, padding: 12 }}>
                    No active tasks for selected objects.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          <MultiSelect
            withinPortal
            label="Select objects to run on"
            data={
              (externalObjects || []).map((obj) => ({
                value: obj.name,
                label: obj.label || obj.name,
              }))
            }
            value={selectedObjects}
            onChange={setSelectedObjects}
            placeholder="Pick one or more objects"
            searchable
            nothingFound="No objects found"
            disabled={isRunning}
            mb="md"
          />

          {/* Date Range Section */}
          <Text size="sm" weight={500} mb="xs">
            Date Range
          </Text>
          <Group mb="md">
            <DatePickerInput
              label="Start Date"
              placeholder="Select start date"
              value={startDate}
              onChange={setStartDate}
              disabled={isRunning}
              style={{ flex: 1 }}
            />
            <DatePickerInput
              label="End Date"
              placeholder="Select end date"
              value={endDate}
              onChange={setEndDate}
              disabled={isRunning}
              style={{ flex: 1 }}
            />
          </Group>

          {/* <Select
            label="Date Field"
            data={[
              { value: 'updatedAt', label: 'Updated At' },
              { value: 'createdAt', label: 'Created At' },
            ]}
            value={dateField}
            onChange={(value) => setDateField(value || 'updatedAt')}
            disabled={isRunning}
            mb="md"
          /> */}

          <Button
            mt="md"
            fullWidth
            onClick={handleRun}
            loading={isRunning}
            disabled={selectedObjects.length === 0 || isRunning || !startDate || !endDate}
          >
            Start Sync Run
          </Button>
        </>
      )}
    </Modal>
  );
}; 